import {
  type Address,
  encodeFunctionData,
  getAddress,
  type Hex,
  isAddressEqual,
  parseSignature,
  parseUnits,
  toFunctionSelector,
} from 'viem'
import type {
  DepositInstructionForChain,
  DepositInstructionsResponse,
  PermitTypedData,
} from '@/hooks/v1'

export const USDC_DECIMALS = 6
export const DEPOSIT_FUNCTION_SIGNATURE =
  'deposit(uint256,bytes16,bytes32,uint256,uint8,bytes32,bytes32)'
export const DEPOSIT_FUNCTION_SELECTOR = toFunctionSelector(DEPOSIT_FUNCTION_SIGNATURE)
export const ZERO_BYTES32 = `0x${'0'.repeat(64)}` as Hex

export const erc20DepositAbi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'decimals',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    type: 'function',
    name: 'nonces',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

export const vaultDepositAbi = [
  {
    type: 'function',
    name: 'deposit',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'quantity', type: 'uint256' },
      { name: 'userId', type: 'bytes16' },
      { name: 'clientReferenceId', type: 'bytes32' },
      { name: 'deadline', type: 'uint256' },
      { name: 'v', type: 'uint8' },
      { name: 'r', type: 'bytes32' },
      { name: 's', type: 'bytes32' },
    ],
    outputs: [],
  },
] as const

const canonicalUuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
// Reject scientific notation and excess precision before converting to integer token units.
const plainUsdcAmountPattern = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/

export const uuidToBytes16 = (uuid: string): Hex => {
  const normalized = uuid.toLowerCase()
  if (!canonicalUuidPattern.test(normalized)) {
    throw new Error('Authenticated user ID is not a canonical UUID')
  }

  return `0x${normalized.replaceAll('-', '')}` as Hex
}

export type AmountValidationResult =
  | { valid: true; baseUnits: bigint }
  | { valid: false; reason: 'invalid' | 'below-minimum' | 'insufficient-usdc' }

export const validateUsdcAmount = (
  quantity: string,
  minimumQuantity: string,
  balance?: bigint,
): AmountValidationResult => {
  if (!plainUsdcAmountPattern.test(quantity)) {
    return { valid: false, reason: 'invalid' }
  }

  let baseUnits: bigint
  let minimumBaseUnits: bigint
  try {
    baseUnits = parseUnits(quantity, USDC_DECIMALS)
    minimumBaseUnits = parseUnits(minimumQuantity, USDC_DECIMALS)
  } catch {
    return { valid: false, reason: 'invalid' }
  }

  if (baseUnits < minimumBaseUnits) {
    return { valid: false, reason: 'below-minimum' }
  }
  if (balance !== undefined && baseUnits > balance) {
    return { valid: false, reason: 'insufficient-usdc' }
  }

  return { valid: true, baseUnits }
}

const canonicalChainId = (value: string | number, field: string): number => {
  const asString = String(value)
  if (!/^\d+$/.test(asString)) throw new Error(`${field} is not a canonical chain ID`)
  const parsed = Number(asString)
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new Error(`${field} is not a safe positive chain ID`)
  }
  return parsed
}

const decimalBigInt = (value: string, field: string): bigint => {
  if (!/^\d+$/.test(value)) throw new Error(`${field} is not a canonical unsigned integer`)
  return BigInt(value)
}

const sameAddress = (left: string, right: string): boolean => {
  try {
    return isAddressEqual(getAddress(left), getAddress(right))
  } catch {
    return false
  }
}

const requireAddress = (actual: string, expected: string, field: string) => {
  if (!sameAddress(actual, expected)) throw new Error(`${field} does not match configuration`)
}

const expectedPermitFields = [
  ['owner', 'address'],
  ['spender', 'address'],
  ['value', 'uint256'],
  ['nonce', 'uint256'],
  ['deadline', 'uint256'],
] as const
const expectedDomainFields = [
  ['name', 'string'],
  ['version', 'string'],
  ['chainId', 'uint256'],
  ['verifyingContract', 'address'],
] as const

const validatePermitTypes = (approval: PermitTypedData) => {
  const validateFields = (
    actual: PermitTypedData['types'][string],
    expected: ReadonlyArray<readonly [string, string]>,
    label: string,
  ) => {
    if (!Array.isArray(actual) || actual.length !== expected.length) {
      throw new Error(`${label} typed data has an unexpected field set`)
    }
    expected.forEach(([name, type], index) => {
      if (actual[index]?.name !== name || actual[index]?.type !== type) {
        throw new Error(`${label} typed data has an unexpected field set`)
      }
    })
  }

  validateFields(approval.types.EIP712Domain, expectedDomainFields, 'EIP-712 domain')
  validateFields(approval.types.Permit, expectedPermitFields, 'Permit')
}

type ValidateInstructionsOptions = {
  response: DepositInstructionsResponse
  userId: string
  ownerAddress: Address
  quantity: string
  nonce: bigint
  chainId: number
  usdcAddress: Address
  vaultAddress: Address
  expirySafetySeconds: number
  nowSeconds?: number
}

// Bind server-provided signing instructions to the current user, wallet, chain, contracts,
// amount, and nonce before asking the wallet to authorize a permit.
export const validateDepositInstructions = ({
  response,
  userId,
  ownerAddress,
  quantity,
  nonce,
  chainId,
  usdcAddress,
  vaultAddress,
  expirySafetySeconds,
  nowSeconds = Math.floor(Date.now() / 1000),
}: ValidateInstructionsOptions): DepositInstructionForChain => {
  if (response.user_id.toLowerCase() !== userId.toLowerCase()) {
    throw new Error('Instructions were issued for another user')
  }
  requireAddress(response.owner_address, ownerAddress, 'Instruction owner')
  if (response.quantity !== quantity) throw new Error('Instruction quantity does not match')
  if (response.chains.length !== 1) throw new Error('Expected instructions for exactly one chain')

  const instruction = response.chains[0]
  const deposit = instruction.deposit
  const approval = instruction.approval
  const instructionChainId = canonicalChainId(instruction.chain_id, 'Instruction chain ID')
  const depositChainId = canonicalChainId(deposit.chain_id, 'Deposit chain ID')
  const domainChainId = canonicalChainId(approval.domain.chainId, 'Permit chain ID')
  if ([instructionChainId, depositChainId, domainChainId].some((value) => value !== chainId)) {
    throw new Error('Instruction chain does not match configuration')
  }

  requireAddress(instruction.usdc_address, usdcAddress, 'Instruction USDC address')
  requireAddress(instruction.contract_address, vaultAddress, 'Instruction vault address')
  requireAddress(deposit.to, vaultAddress, 'Deposit destination')
  requireAddress(approval.domain.verifyingContract, usdcAddress, 'Permit verifying contract')
  requireAddress(approval.message.owner, ownerAddress, 'Permit owner')
  requireAddress(approval.message.spender, vaultAddress, 'Permit spender')

  if (approval.primaryType !== 'Permit') throw new Error('Unexpected permit primary type')
  validatePermitTypes(approval)

  const expectedQuantity = parseUnits(quantity, USDC_DECIMALS)
  if (decimalBigInt(approval.message.value, 'Permit value') !== expectedQuantity) {
    throw new Error('Permit value does not match the requested quantity')
  }
  if (decimalBigInt(deposit.args.quantity, 'Deposit quantity') !== expectedQuantity) {
    throw new Error('Deposit quantity does not match the requested quantity')
  }
  if (decimalBigInt(approval.message.nonce, 'Permit nonce') !== nonce) {
    throw new Error('Permit nonce is stale or unexpected')
  }
  if (deposit.value !== '0') throw new Error('Deposit unexpectedly sends native currency')
  if (deposit.function_signature !== DEPOSIT_FUNCTION_SIGNATURE) {
    throw new Error('Unexpected vault deposit function')
  }
  if (deposit.selector.toLowerCase() !== DEPOSIT_FUNCTION_SELECTOR.toLowerCase()) {
    throw new Error('Vault deposit selector does not match its function signature')
  }
  if (deposit.args.user_id.toLowerCase() !== uuidToBytes16(userId).toLowerCase()) {
    throw new Error('Deposit user ID does not match the authenticated user')
  }
  if (deposit.args.client_reference_id.toLowerCase() !== ZERO_BYTES32) {
    throw new Error('Unexpected client reference in deposit instructions')
  }

  const permitDeadline = decimalBigInt(approval.message.deadline, 'Permit deadline')
  const depositDeadline = decimalBigInt(deposit.args.deadline, 'Deposit deadline')
  if (permitDeadline !== depositDeadline) throw new Error('Permit and deposit deadlines differ')
  // Leave time for wallet approval and submission rather than accepting a nearly expired permit.
  if (permitDeadline <= BigInt(nowSeconds + expirySafetySeconds)) {
    throw new Error('Deposit instructions are too close to expiry')
  }

  return instruction
}

export const encodeDepositCalldata = (
  instruction: DepositInstructionForChain,
  signature: Hex,
): Hex => {
  const parsed = parseSignature(signature)
  const v = parsed.v ?? BigInt((parsed.yParity ?? 0) + 27)
  if (v !== 27n && v !== 28n) throw new Error('Permit signature has an invalid recovery value')

  return encodeFunctionData({
    abi: vaultDepositAbi,
    functionName: 'deposit',
    args: [
      BigInt(instruction.deposit.args.quantity),
      instruction.deposit.args.user_id as Hex,
      instruction.deposit.args.client_reference_id as Hex,
      BigInt(instruction.deposit.args.deadline),
      Number(v),
      parsed.r,
      parsed.s,
    ],
  })
}
